import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { TlkBannerComponent } from '@itero/ui-toolkit-angular/banner';
import { AiCrownIconComponent } from '@itero/ui-toolkit-angular/icons';
import { RxIntuitiveTreatmentToolbarComponent } from '@modules/rx-intuitive-teeth-orchestrator/components/rx-intuitive-treatment-toolbar/rx-intuitive-treatment-toolbar.component';
import { RxIntuitiveTeethDiagramFooterComponent } from '@modules/rx-intuitive-teeth-orchestrator/components/rx-intuitive-teeth-diagram-footer/rx-intuitive-teeth-diagram-footer.component';
import { RxIntuitiveToothEditorContainerComponent } from '@modules/rx-intuitive-teeth-orchestrator/containers/rx-intuitive-tooth-editor-container/rx-intuitive-tooth-editor-container.component';
import { RxIntuitiveIndicationMapperService } from '@modules/rx-intuitive-teeth-orchestrator/services/rx-intuitive-indication-mapper.service';
import { OrchestratorViewModeType } from '@modules/rx-intuitive-teeth-orchestrator/state/rx-intuitive-teeth-orchestrator-store';
import { TranslateModule } from '@ngx-translate/core';
import { BaseDestroyableComponent } from '@shared/base-classes/base-destroyable';
import { CrownAiBannerVisibilityService } from '@shared/services/crown-ai-banner-visibility.service';
import { combineLatest, fromEvent } from 'rxjs';
import { filter, map, takeUntil, withLatestFrom } from 'rxjs/operators';
import { RxIntuitiveIndicationButtonsComponent } from '../../components/rx-intuitive-indication-buttons/rx-intuitive-indication-buttons.component';
import { RxIntuitiveTeethDiagramComponent } from '../../components/rx-intuitive-teeth-diagram/rx-intuitive-teeth-diagram.component';
import { IntuitiveIndication } from '../../models';
import { RxIntuitiveTeethOrchestratorFacade } from '../../rx-intuitive-teeth-orchestrator.facade';
import { DefaultStrategy } from '../../services/intuitive-teeth-diagram-button-strategy/intuitive-indication.strategy';
import { RxIntuitiveCopyFromBehaviorService } from '../../services/rx-intuitive-copy-from-behavior.service';
import { RxIntuitiveTeethOrchestratorLayoutComponent } from '../rx-intuitive-teeth-orchestrator-layout/rx-intuitive-teeth-orchestrator-layout.component';

@Component({
	selector: 'rx-intuitive-teeth-orchestrator',
	templateUrl: './rx-intuitive-teeth-orchestrator.component.html',
	styleUrls: ['./rx-intuitive-teeth-orchestrator.component.scss'],
	standalone: true,
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [
		CommonModule,
		TranslateModule,
		RxIntuitiveTeethDiagramComponent,
		RxIntuitiveIndicationButtonsComponent,
		RxIntuitiveTeethDiagramFooterComponent,
		RxIntuitiveTreatmentToolbarComponent,
		TlkBannerComponent,
		AiCrownIconComponent,
		RxIntuitiveToothEditorContainerComponent,
		RxIntuitiveTeethOrchestratorLayoutComponent
	]
})
export class RxIntuitiveTeethOrchestratorComponent extends BaseDestroyableComponent implements OnInit {
	@ViewChild(RxIntuitiveTeethDiagramComponent, { read: ElementRef }) private readonly teethDiagram?: ElementRef<HTMLElement>;

	readonly orchestratorViewMode$ = this.facade.orchestratorViewMode$;

	readonly indications$ = this.facade.indicationToolbarIds$;
	readonly enabledIndications$ = this.facade.enabledIndications$;
	readonly upperJaw$ = this.facade.upperJaw$;
	readonly lowerJaw$ = this.facade.lowerJaw$;
	readonly hasSelectedTeeth$ = this.facade.hasSelectedTeeth$;
	readonly selectedTeethIndicationIds$ = this.facade.selectedTeethIndicationIds$;
	readonly selectedToothIds$ = this.facade.selectedToothIds$;

	private readonly pointerUp$ = fromEvent<PointerEvent>(document, 'pointerup');
	readonly isTeethDiagramReadOnly$ = this.facade.isTeethDiagramReadOnly$;

	/** True when the list, bridge summary rows, or full editor is shown (placeholder hidden). */
	readonly hasSidePanelContent$ = combineLatest([this.facade.previewTreatmentListItems$, this.facade.isToothEditorShown$]).pipe(
		map(([items, editorShown]) => items.length > 0 || editorShown)
	);

	readonly showCopyFromMenu$ = this.isTeethDiagramReadOnly$.pipe(map(readOnly => !readOnly));

	readonly chartReadOnly$ = this.isTeethDiagramReadOnly$.pipe(map(readOnly => readOnly));

	readonly indicationButtonsDisabled$ = combineLatest([this.hasSelectedTeeth$, this.isTeethDiagramReadOnly$]).pipe(
		map(([hasSelected, readOnly]) => hasSelected !== true || !!readOnly)
	);

	readonly indicationButtonsAsLabel$ = this.isTeethDiagramReadOnly$.pipe(map(readOnly => readOnly));

	readonly showPanelBack$ = this.hasSidePanelContent$;

	readonly isCrownAIBannerVisible$ = this.crownAiBannerVisibilityService.isVisible$;

	readonly isTableViewDisabled$ = this.facade.previewTreatmentListItems$.pipe(map(items => items.length === 0));

	readonly preTreatmentValidationError$ = this.facade.preTreatmentValidationError$;

	constructor(
		protected readonly facade: RxIntuitiveTeethOrchestratorFacade,
		protected readonly copyFromBehaviorService: RxIntuitiveCopyFromBehaviorService,
		protected readonly crownAiBannerVisibilityService: CrownAiBannerVisibilityService,
		protected readonly indicationMapper: RxIntuitiveIndicationMapperService,
		protected readonly defaultStrategy: DefaultStrategy
	) {
		super();
	}

	ngOnInit(): void {
		this.pointerUp$
			.pipe(
				takeUntil(this.componentAlive$),
				withLatestFrom(this.facade.hasSelectedTeeth$),
				filter(([event, hasSelectedTeeth]) => hasSelectedTeeth && !this.isInsideTeethEditingArea(event.target))
			)
			.subscribe(() => this.facade.clearToothSelection());
	}

	onToothClicked(toothId: number): void {
		this.facade.toggleToothSelection(toothId);
	}

	onResetSelectedRequested(): void {
		this.facade.clearToothSelection();
	}

	onIndicationSelected(indicationId: IntuitiveIndication): void {
		this.defaultStrategy.teethDiagramButtonHandle(indicationId).pipe(takeUntil(this.componentAlive$)).subscribe();
	}

	focusDiagram(): void {
		this.teethDiagram?.nativeElement?.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
		this.teethDiagram?.nativeElement?.focus({ preventScroll: true });
	}

	changeOrchestratorViewMode(newViewMode: OrchestratorViewModeType) {
		this.facade.changeViewMode(newViewMode);
	}

	private isInsideTeethEditingArea(target: EventTarget | null): boolean {
		const editingAreaComponentSelectors = [
			'.cdk-overlay-container',
			'rx-intuitive-implants-view-modal',
			'rx-copy-from-tooth-menu',
			'rx-intuitive-tooth-editor',
			'rx-intuitive-bridge-editor',
			'rx-intuitive-teeth-diagram',
			'rx-intuitive-teeth-treatment-viewlist',
			'rx-intuitive-indication-button',
			'rx-intuitive-remove-from-bridge-button',
			'[rx-intuitive-bridge-indication-buttons] button',
			'[rx-intuitive-teeth-diagram-footer] button',
			'[rx-intuitive-teeth-diagram-banner]'
		];

		if (target == null) {
			return false;
		}

		let element: Element;

		if (target instanceof Element) {
			element = target;
		} else if (target instanceof Node && target.parentElement != null) {
			element = target.parentElement;
		} else {
			return false;
		}

		return editingAreaComponentSelectors.some(s => element.closest(s) != null);
	}
}
